import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Stack,
    Button,
    Paper,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Save, RestartAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import WrapperSection from '../../components/global/WrapperSection';
import GlobalBreadcrumbs from '../../components/global/GlobalBreadcrumbs';
import SwitchInput from '../../components/inputs/SwitchInput';
import PaymobSettingsCard, {
    MASK_SENTINEL,
} from '../../components/settings/PaymobSettingsCard';
import { useGetSettings } from '../../hooks/settings/useGetSettings';
import { useUpdateSettings } from '../../hooks/settings/useUpdateSettings';

// Backend returns payment settings as a flat map with dot-notation keys
// (e.g. { "paymob.api_key": "****", "paymob.integration_id": "..." }).
// The cards prefer a nested shape, so we normalize on read and denormalize
// on save.
const PROVIDERS = ['paymob', 'cod', 'manual'];

const nestPayment = (flat) => {
    if (!flat || typeof flat !== 'object') return {};
    const nested = {};
    Object.entries(flat).forEach(([key, value]) => {
        const dot = key.indexOf('.');
        if (dot > -1) {
            const provider = key.slice(0, dot);
            const field = key.slice(dot + 1);
            nested[provider] = { ...(nested[provider] || {}), [field]: value };
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            // Already-nested provider block — keep as-is.
            nested[key] = { ...(nested[key] || {}), ...value };
        } else {
            nested[key] = value;
        }
    });
    return nested;
};

const flattenPayment = (nested) => {
    const flat = {};
    Object.entries(nested || {}).forEach(([provider, value]) => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([field, fieldValue]) => {
                flat[`${provider}.${field}`] = fieldValue;
            });
        } else {
            flat[provider] = value;
        }
    });
    return flat;
};

// Build a partial PATCH payload by diffing draft against original.
// For paymob secrets, fields whose value is still the MASK_SENTINEL are
// considered "untouched" and excluded from the payload.
const buildPaymentPatch = (original, draft) => {
    const origPayment = nestPayment(original?.payment);
    const nextPayment = draft?.payment || {};
    const patch = {};

    PROVIDERS.forEach((provider) => {
        const orig = origPayment[provider] || {};
        const next = nextPayment[provider] || {};
        const changed = {};

        Object.keys(next).forEach((field) => {
            const a = orig[field];
            const b = next[field];

            if (b === MASK_SENTINEL) return;
            if (JSON.stringify(a) === JSON.stringify(b)) return;

            changed[field] = b;
        });

        if (Object.keys(changed).length > 0) {
            patch[provider] = changed;
        }
    });

    if (Object.keys(patch).length === 0) return {};
    return { payment: flattenPayment(patch) };
};

const SimpleProviderCard = ({ titleEn, titleAr, descriptionEn, descriptionAr, data, onChange, disabled }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const safe = data && typeof data === 'object' ? data : {};

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
            }}
        >
            <Box mb={2}>
                <Typography variant="h6" fontWeight={700}>
                    {isArabic ? titleAr : titleEn}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {isArabic ? descriptionAr : descriptionEn}
                </Typography>
            </Box>

            <SwitchInput
                label={isArabic ? 'مفعّل' : 'Enabled'}
                name="is_enabled"
                checked={Boolean(safe.is_enabled)}
                onChange={(e) => onChange({ ...safe, is_enabled: e.target.checked })}
                disabled={disabled}
            />
        </Paper>
    );
};

const PaymentSettings = () => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { settings, isLoading, error } = useGetSettings();
    const updateMutation = useUpdateSettings();

    const [draft, setDraft] = useState({});

    useEffect(() => {
        if (!settings) {
            setDraft({});
            return;
        }
        setDraft({
            ...settings,
            payment: nestPayment(settings.payment),
        });
    }, [settings]);

    const patch = useMemo(() => buildPaymentPatch(settings, draft), [settings, draft]);
    const isDirty = Object.keys(patch).length > 0;

    const updateProvider = (provider) => (next) => {
        setDraft((prev) => ({
            ...prev,
            payment: {
                ...(prev?.payment || {}),
                [provider]: next,
            },
        }));
    };

    const handleReset = () => setDraft(settings ? { ...settings, payment: nestPayment(settings.payment) } : {});
    const handleSave = () => {
        if (!isDirty) return;
        updateMutation.mutate(patch);
    };

    const paymentDraft = draft?.payment || {};

    return (
        <WrapperSection>
            <GlobalBreadcrumbs />

            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight={700}>
                    {isArabic ? 'إعدادات الدفع' : 'Payment Settings'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {isArabic
                        ? 'تفعيل أو إيقاف بوابات الدفع وضبط مفاتيح الـ API. الأسرار تظهر مشفّرة افتراضياً.'
                        : 'Enable or disable payment providers and set their API keys. Secrets are masked by default.'}
                </Typography>
            </Box>

            {isLoading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                </Box>
            ) : error ? (
                <Alert severity="error">
                    {isArabic ? 'تعذر تحميل الإعدادات' : 'Failed to load settings'}
                </Alert>
            ) : (
                <>
                    <PaymobSettingsCard
                        data={paymentDraft.paymob}
                        onChange={updateProvider('paymob')}
                        disabled={updateMutation.isPending}
                    />

                    <SimpleProviderCard
                        titleEn="Cash on Delivery"
                        titleAr="الدفع عند الاستلام"
                        descriptionEn="Toggle COD availability at checkout."
                        descriptionAr="تفعيل خيار الدفع عند الاستلام في الشيك آوت."
                        data={paymentDraft.cod}
                        onChange={updateProvider('cod')}
                        disabled={updateMutation.isPending}
                    />

                    <SimpleProviderCard
                        titleEn="Manual / Bank transfer"
                        titleAr="تحويل بنكي / يدوي"
                        descriptionEn="Toggle manual payment availability for admin-created orders."
                        descriptionAr="تفعيل الدفع اليدوي للطلبات التي تُنشأ من الإدارة."
                        data={paymentDraft.manual}
                        onChange={updateProvider('manual')}
                        disabled={updateMutation.isPending}
                    />

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        sx={{ mt: 3 }}
                        justifyContent="flex-end"
                    >
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<RestartAlt />}
                            disabled={!isDirty || updateMutation.isPending}
                            onClick={handleReset}
                        >
                            {isArabic ? 'استعادة' : 'Reset'}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Save />}
                            disabled={!isDirty || updateMutation.isPending}
                            onClick={handleSave}
                        >
                            {updateMutation.isPending
                                ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                                : (isArabic ? 'حفظ التغييرات' : 'Save changes')}
                        </Button>
                    </Stack>
                </>
            )}
        </WrapperSection>
    );
};

export default PaymentSettings;
